import { measureText } from './helvetica-metrics';

/**
 * Gerador de PDF mínimo, sem dependências externas.
 *
 * Usa as fontes base-14 do PDF (Helvetica e Helvetica-Bold), que todo leitor já possui: nada de
 * fonte embutida, nada de rasterizar a tela em imagem. O texto sai vetorial e selecionável, e o
 * arquivo fica na casa de dezenas de KB.
 *
 * O escopo é estreito de propósito — texto, caixas arredondadas com marcador e linhas de
 * tabela. Se o documento precisar de imagens, colunas ou fontes próprias, o caminho é trocar
 * por uma biblioteca (pdfmake), não aumentar este arquivo.
 */

/** A4 em pontos tipográficos (1 pt = 1/72"). */
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const MARGIN = 56;

const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const CONTENT_TOP = PAGE_HEIGHT - MARGIN;
const USABLE_HEIGHT = CONTENT_TOP - MARGIN;

const LINE_HEIGHT_RATIO = 1.35;
const DEFAULT_FONT_SIZE = 11;

/**
 * Quebramos a linha a 98% da largura disponível: as larguras de glifos acentuados são
 * aproximadas (ver `helvetica-metrics`) e essa folga impede que alguma linha estoure a caixa.
 */
const WRAP_SAFETY = 0.98;

/** Aproximação de círculo e de canto arredondado por Bézier cúbica. */
const KAPPA = 0.5523;

export type PdfFontStyle = 'regular' | 'bold';

/** Componentes RGB de 0 a 1, como o PDF espera. */
export type PdfColor = readonly [number, number, number];

const DEFAULT_COLOR: PdfColor = [0.1, 0.1, 0.1];

export interface PdfTextRun {
  text: string;
  font?: PdfFontStyle;
  size?: number;
  color?: PdfColor;
}

/** Parágrafo solto no fluxo. */
export interface PdfTextItem {
  kind: 'text';
  run: PdfTextRun;
  indent?: number;
  spacingBefore?: number;
  spacingAfter?: number;
}

/**
 * Alternativa de pergunta: caixa arredondada ocupando a largura útil, com marcador circular à
 * esquerda, o texto ao lado e um complemento opcional alinhado à direita.
 */
export interface PdfOptionItem {
  kind: 'option';
  run: PdfTextRun;
  trailing?: PdfTextRun;
  spacingBefore?: number;
}

/** Linha de tabela: células em colunas fixas, com divisória opcional abaixo. */
export interface PdfRowItem {
  kind: 'row';
  cells: readonly PdfTextRun[];
  /** Início de cada coluna como fração (0 a 1) da largura útil. Um valor por célula. */
  columnStarts: readonly number[];
  dividerColor?: PdfColor;
  spacingBefore?: number;
}

export type PdfItem = PdfTextItem | PdfOptionItem | PdfRowItem;

export interface PdfBlock {
  items: readonly PdfItem[];
  /**
   * Mantém o bloco inteiro na mesma página quando ele couber em uma — é o que impede uma
   * pergunta de ser separada das suas alternativas. Blocos mais altos que uma página são
   * divididos normalmente.
   */
  keepTogether?: boolean;
}

/** Aparência das caixas de alternativa, espelhando o layout da tela. */
const OPTION_STYLE = {
  paddingX: 12,
  paddingY: 9,
  cornerRadius: 6,
  borderColor: [0.898, 0.898, 0.898] as PdfColor,
  fillColor: [1, 1, 1] as PdfColor,
  borderWidth: 0.8,
  markerRadius: 5,
  markerColor: [0.898, 0.898, 0.898] as PdfColor,
  markerBorderWidth: 1.2,
  /** Distâncias medidas a partir da borda esquerda da caixa. */
  markerCenterOffset: 20,
  textOffset: 36,
  gapBetweenOptions: 6,
} as const;

const ROW_STYLE = {
  paddingY: 7,
  dividerWidth: 0.6,
} as const;

// ── Desenhos ───────────────────────────────────────────────────────────────────
// `offsetY` é sempre a distância, para baixo, entre o topo da área útil e a âncora do desenho:
// a linha de base no texto, o topo no retângulo e o centro no círculo.

interface DrawText {
  kind: 'text';
  text: string;
  font: PdfFontStyle;
  size: number;
  color: PdfColor;
  x: number;
  offsetY: number;
}

interface DrawRoundedRect {
  kind: 'rect';
  x: number;
  width: number;
  height: number;
  cornerRadius: number;
  fill?: PdfColor;
  stroke?: PdfColor;
  lineWidth: number;
  offsetY: number;
}

interface DrawCircle {
  kind: 'circle';
  centerX: number;
  radius: number;
  stroke: PdfColor;
  lineWidth: number;
  offsetY: number;
}

interface DrawLine {
  kind: 'line';
  fromX: number;
  toX: number;
  stroke: PdfColor;
  lineWidth: number;
  offsetY: number;
}

type Drawable = DrawText | DrawRoundedRect | DrawCircle | DrawLine;

/** Unidade atômica de paginação: nunca é dividida entre duas páginas. */
interface LayoutUnit {
  height: number;
  /** Aqui o `offsetY` de cada desenho é relativo ao topo da própria unidade. */
  drawables: readonly Drawable[];
}

// ── Quebra de linha ────────────────────────────────────────────────────────────

function wrapText(text: string, size: number, bold: boolean, maxWidth: number): string[] {
  const limit = maxWidth * WRAP_SAFETY;
  const lines: string[] = [];

  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(/\s+/).filter(Boolean);

    if (words.length === 0) {
      lines.push('');
      continue;
    }

    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;

      if (measureText(candidate, size, bold) <= limit || !current) {
        current = candidate;
        continue;
      }

      lines.push(current);
      current = word;
    }

    lines.push(current);
  }

  return lines;
}

interface ResolvedRun {
  font: PdfFontStyle;
  size: number;
  color: PdfColor;
  lineHeight: number;
}

function resolveRun(run: PdfTextRun): ResolvedRun {
  const size = run.size ?? DEFAULT_FONT_SIZE;

  return {
    size,
    font: run.font ?? 'regular',
    color: run.color ?? DEFAULT_COLOR,
    lineHeight: size * LINE_HEIGHT_RATIO,
  };
}

function measureRun(run: PdfTextRun): number {
  const { size, font } = resolveRun(run);
  return measureText(run.text, size, font === 'bold');
}

function textDrawable(run: PdfTextRun, x: number, offsetY: number, text = run.text): DrawText {
  const { font, size, color } = resolveRun(run);
  return { kind: 'text', text, font, size, color, x, offsetY };
}

// ── Layout de cada tipo de item ────────────────────────────────────────────────

function layoutTextItem(item: PdfTextItem): LayoutUnit {
  const { font, size, lineHeight } = resolveRun(item.run);
  const indent = item.indent ?? 0;

  const drawables: DrawText[] = [];
  let cursor = item.spacingBefore ?? 0;

  for (const line of wrapText(item.run.text, size, font === 'bold', CONTENT_WIDTH - indent)) {
    cursor += lineHeight;
    drawables.push(textDrawable(item.run, MARGIN + indent, cursor, line));
  }

  return { height: cursor + (item.spacingAfter ?? 0), drawables };
}

function layoutOptionItem(item: PdfOptionItem): LayoutUnit {
  const { font, size, lineHeight } = resolveRun(item.run);
  const { paddingX, paddingY, textOffset, markerCenterOffset } = OPTION_STYLE;

  const trailingWidth = item.trailing ? measureRun(item.trailing) + paddingX : 0;
  const textWidth = CONTENT_WIDTH - textOffset - paddingX - trailingWidth;
  const lines = wrapText(item.run.text, size, font === 'bold', textWidth);

  const boxHeight = paddingY * 2 + lines.length * lineHeight;
  const boxTop = item.spacingBefore ?? OPTION_STYLE.gapBetweenOptions;
  /** Desconto para a linha de base cair no meio óptico da linha, não no seu topo. */
  const firstBaseline = boxTop + paddingY + lineHeight * 0.75;

  const drawables: Drawable[] = [
    {
      kind: 'rect',
      x: MARGIN,
      width: CONTENT_WIDTH,
      height: boxHeight,
      cornerRadius: OPTION_STYLE.cornerRadius,
      fill: OPTION_STYLE.fillColor,
      stroke: OPTION_STYLE.borderColor,
      lineWidth: OPTION_STYLE.borderWidth,
      offsetY: boxTop,
    },
    {
      kind: 'circle',
      centerX: MARGIN + markerCenterOffset,
      radius: OPTION_STYLE.markerRadius,
      stroke: OPTION_STYLE.markerColor,
      lineWidth: OPTION_STYLE.markerBorderWidth,
      offsetY: boxTop + boxHeight / 2,
    },
    ...lines.map((line, index) =>
      textDrawable(item.run, MARGIN + textOffset, firstBaseline + index * lineHeight, line),
    ),
  ];

  if (item.trailing) {
    const x = MARGIN + CONTENT_WIDTH - paddingX - measureRun(item.trailing);
    drawables.push(textDrawable(item.trailing, x, firstBaseline));
  }

  return { height: boxTop + boxHeight, drawables };
}

function layoutRowItem(item: PdfRowItem): LayoutUnit {
  const lineHeight = Math.max(...item.cells.map((cell) => resolveRun(cell).lineHeight));
  const top = item.spacingBefore ?? 0;
  const baseline = top + ROW_STYLE.paddingY + lineHeight;

  const drawables: Drawable[] = item.cells.map((cell, index) =>
    textDrawable(cell, MARGIN + CONTENT_WIDTH * (item.columnStarts[index] ?? 0), baseline),
  );

  const height = top + ROW_STYLE.paddingY * 2 + lineHeight;

  if (item.dividerColor) {
    drawables.push({
      kind: 'line',
      fromX: MARGIN,
      toX: MARGIN + CONTENT_WIDTH,
      stroke: item.dividerColor,
      lineWidth: ROW_STYLE.dividerWidth,
      offsetY: height,
    });
  }

  return { height, drawables };
}

function layoutItem(item: PdfItem): LayoutUnit {
  switch (item.kind) {
    case 'option':
      return layoutOptionItem(item);
    case 'row':
      return layoutRowItem(item);
    default:
      return layoutTextItem(item);
  }
}

/** Distribui as unidades em páginas, respeitando `keepTogether`. */
function paginate(blocks: readonly PdfBlock[]): Drawable[][] {
  const pages: Drawable[][] = [];

  let current: Drawable[] = [];
  let usedHeight = 0;

  const breakPage = () => {
    pages.push(current);
    current = [];
    usedHeight = 0;
  };

  for (const block of blocks) {
    const units = block.items.map(layoutItem);
    const blockHeight = units.reduce((total, unit) => total + unit.height, 0);

    const fitsInOnePage = blockHeight <= USABLE_HEIGHT;
    const overflowsHere = usedHeight + blockHeight > USABLE_HEIGHT;

    if (block.keepTogether && fitsInOnePage && overflowsHere && current.length) {
      breakPage();
    }

    for (const unit of units) {
      if (usedHeight + unit.height > USABLE_HEIGHT && current.length) {
        breakPage();
      }

      const unitTop = usedHeight;
      for (const drawable of unit.drawables) {
        current.push({ ...drawable, offsetY: unitTop + drawable.offsetY });
      }

      usedHeight += unit.height;
    }
  }

  if (current.length) {
    pages.push(current);
  }

  return pages.length ? pages : [[]];
}

// ── Codificação de texto ───────────────────────────────────────────────────────

function escapePdfText(text: string): string {
  return text.replace(/([\\()])/g, '\\$1');
}

/** Caracteres do CP1252 que não coincidem com o code point Unicode. */
const WIN_ANSI_EXCEPTIONS: Readonly<Record<string, number>> = {
  '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85,
  '†': 0x86, '‡': 0x87, 'ˆ': 0x88, '‰': 0x89, 'Š': 0x8a,
  '‹': 0x8b, 'Œ': 0x8c, 'Ž': 0x8e, '‘': 0x91, '’': 0x92,
  '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97,
  '˜': 0x98, '™': 0x99, 'š': 0x9a, '›': 0x9b, 'œ': 0x9c,
  'ž': 0x9e, 'Ÿ': 0x9f,
};

const QUESTION_MARK = 0x3f;

/**
 * Converte para WinAnsi (CP1252), a codificação declarada nas fontes. Os acentos do português
 * ficam todos na faixa Latin-1, onde o code point coincide com o byte; o que passa disso vira
 * `?` em vez de corromper o arquivo.
 */
function toWinAnsi(text: string): string {
  let encoded = '';

  for (const char of text) {
    const exception = WIN_ANSI_EXCEPTIONS[char];

    if (exception != null) {
      encoded += String.fromCharCode(exception);
      continue;
    }

    const code = char.codePointAt(0) ?? QUESTION_MARK;
    encoded += String.fromCharCode(code <= 0xff ? code : QUESTION_MARK);
  }

  return encoded;
}

// ── Operadores gráficos ────────────────────────────────────────────────────────

const formatNumber = (value: number): string => value.toFixed(2);
const formatColor = (color: PdfColor): string => color.map((c) => c.toFixed(3)).join(' ');

/** Contorno de retângulo arredondado, a partir do canto inferior esquerdo. */
function roundedRectPath(x: number, y: number, width: number, height: number, r: number): string {
  const radius = Math.min(r, width / 2, height / 2);
  const k = radius * KAPPA;
  const p = formatNumber;

  return [
    `${p(x + radius)} ${p(y)} m`,
    `${p(x + width - radius)} ${p(y)} l`,
    `${p(x + width - radius + k)} ${p(y)} ${p(x + width)} ${p(y + radius - k)} ${p(x + width)} ${p(y + radius)} c`,
    `${p(x + width)} ${p(y + height - radius)} l`,
    `${p(x + width)} ${p(y + height - radius + k)} ${p(x + width - radius + k)} ${p(y + height)} ${p(x + width - radius)} ${p(y + height)} c`,
    `${p(x + radius)} ${p(y + height)} l`,
    `${p(x + radius - k)} ${p(y + height)} ${p(x)} ${p(y + height - radius + k)} ${p(x)} ${p(y + height - radius)} c`,
    `${p(x)} ${p(y + radius)} l`,
    `${p(x)} ${p(y + radius - k)} ${p(x + radius - k)} ${p(y)} ${p(x + radius)} ${p(y)} c`,
    'h',
  ].join('\n');
}

function circlePath(centerX: number, centerY: number, radius: number): string {
  const k = radius * KAPPA;
  const p = formatNumber;

  return [
    `${p(centerX - radius)} ${p(centerY)} m`,
    `${p(centerX - radius)} ${p(centerY + k)} ${p(centerX - k)} ${p(centerY + radius)} ${p(centerX)} ${p(centerY + radius)} c`,
    `${p(centerX + k)} ${p(centerY + radius)} ${p(centerX + radius)} ${p(centerY + k)} ${p(centerX + radius)} ${p(centerY)} c`,
    `${p(centerX + radius)} ${p(centerY - k)} ${p(centerX + k)} ${p(centerY - radius)} ${p(centerX)} ${p(centerY - radius)} c`,
    `${p(centerX - k)} ${p(centerY - radius)} ${p(centerX - radius)} ${p(centerY - k)} ${p(centerX - radius)} ${p(centerY)} c`,
    'h',
  ].join('\n');
}

function textToOps(drawable: DrawText): string {
  const fontRef = drawable.font === 'bold' ? '/F2' : '/F1';
  const y = CONTENT_TOP - drawable.offsetY;
  const text = escapePdfText(toWinAnsi(drawable.text));

  return (
    `BT ${formatColor(drawable.color)} rg ${fontRef} ${drawable.size} Tf ` +
    `1 0 0 1 ${formatNumber(drawable.x)} ${formatNumber(y)} Tm (${text}) Tj ET`
  );
}

function shapePath(drawable: DrawRoundedRect | DrawCircle | DrawLine): string {
  const y = CONTENT_TOP - drawable.offsetY;

  switch (drawable.kind) {
    case 'rect':
      // A origem do PDF fica no canto inferior esquerdo, daí o `- height`.
      return roundedRectPath(
        drawable.x,
        y - drawable.height,
        drawable.width,
        drawable.height,
        drawable.cornerRadius,
      );
    case 'circle':
      return circlePath(drawable.centerX, y, drawable.radius);
    default:
      return `${formatNumber(drawable.fromX)} ${formatNumber(y)} m ${formatNumber(drawable.toX)} ${formatNumber(y)} l`;
  }
}

function shapeToOps(drawable: DrawRoundedRect | DrawCircle | DrawLine): string {
  const fill = drawable.kind === 'rect' ? drawable.fill : undefined;
  const paintOperator = fill && drawable.stroke ? 'B' : fill ? 'f' : 'S';

  const ops = ['q'];
  if (fill) ops.push(`${formatColor(fill)} rg`);
  if (drawable.stroke) ops.push(`${formatColor(drawable.stroke)} RG`);
  ops.push(`${formatNumber(drawable.lineWidth)} w`, shapePath(drawable), paintOperator, 'Q');

  return ops.join('\n');
}

function contentStreamFor(drawables: readonly Drawable[]): string {
  // Formas primeiro, texto depois: uma caixa desenhada por último cobriria o texto que emoldura.
  const shapes = drawables.filter((d): d is Exclude<Drawable, DrawText> => d.kind !== 'text');
  const texts = drawables.filter((d): d is DrawText => d.kind === 'text');

  return [...shapes.map(shapeToOps), ...texts.map(textToOps)].join('\n');
}

// ── Montagem do arquivo ────────────────────────────────────────────────────────

const OBJ_CATALOG = 1;
const OBJ_PAGES = 2;
const OBJ_FONT_REGULAR = 3;
const OBJ_FONT_BOLD = 4;
const OBJ_INFO = 5;
const FIRST_PAGE_OBJ = 6;

const pageObjId = (index: number): number => FIRST_PAGE_OBJ + index * 2;
const contentObjId = (index: number): number => FIRST_PAGE_OBJ + index * 2 + 1;

/** Objetos indexados pelo próprio número; a posição 0 fica vazia, como manda o formato. */
function buildObjects(pages: readonly Drawable[][], title: string): string[] {
  const objects: string[] = [];
  const put = (id: number, body: string) => {
    objects[id] = `${id} 0 obj\n${body}\nendobj\n`;
  };

  const kids = pages.map((_, i) => `${pageObjId(i)} 0 R`).join(' ');

  put(OBJ_CATALOG, `<< /Type /Catalog /Pages ${OBJ_PAGES} 0 R >>`);
  put(OBJ_PAGES, `<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`);
  put(
    OBJ_FONT_REGULAR,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
  );
  put(
    OBJ_FONT_BOLD,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
  );
  put(
    OBJ_INFO,
    `<< /Title (${escapePdfText(toWinAnsi(title))}) /Producer (perfil-investimento-adm) >>`,
  );

  pages.forEach((drawables, i) => {
    const stream = contentStreamFor(drawables);

    put(
      pageObjId(i),
      `<< /Type /Page /Parent ${OBJ_PAGES} 0 R ` +
        `/MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
        `/Resources << /Font << /F1 ${OBJ_FONT_REGULAR} 0 R /F2 ${OBJ_FONT_BOLD} 0 R >> >> ` +
        `/Contents ${contentObjId(i)} 0 R >>`,
    );
    put(contentObjId(i), `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  return objects;
}

/**
 * A tabela `xref` precisa do offset exato, em bytes, de cada objeto — por isso o arquivo é
 * montado acumulando o comprimento conforme os objetos são emitidos.
 */
function serializeDocument(objects: readonly string[]): string {
  let file = '%PDF-1.4\n';
  const offsets: number[] = [];

  for (let id = 1; id < objects.length; id++) {
    offsets[id] = file.length;
    file += objects[id];
  }

  const objectCount = objects.length;
  const xrefStart = file.length;

  file += `xref\n0 ${objectCount}\n0000000000 65535 f \n`;
  for (let id = 1; id < objectCount; id++) {
    file += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`;
  }

  file += `trailer\n<< /Size ${objectCount} /Root ${OBJ_CATALOG} 0 R /Info ${OBJ_INFO} 0 R >>\n`;
  file += `startxref\n${xrefStart}\n%%EOF\n`;

  return file;
}

/** Cada caractere já está na faixa 0..255 (WinAnsi), então o code point é o próprio byte. */
function toBytes(file: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(file.length);

  for (let i = 0; i < file.length; i++) {
    bytes[i] = file.charCodeAt(i) & 0xff;
  }

  return bytes;
}

export function createPdfBlob(blocks: readonly PdfBlock[], title = ''): Blob {
  const pages = paginate(blocks);
  const file = serializeDocument(buildObjects(pages, title));

  return new Blob([toBytes(file)], { type: 'application/pdf' });
}
